import type { StrapiQuotationRow } from '../../api/strapiCrm';
import {
  quotationFormFinancialBreakdown,
  quotationFormFlatSamplesFromDetailLines,
  quotationFormGroupsFromDetailLines,
  quotationPreviewSampleSectionsFromRow,
  type QuotationFormFinancialBreakdown,
  type QuotationPreviewSampleSection,
} from './QuotationFormRoute/quotationFormGroups';
import { quotationDatetimeLocalFromIso } from './QuotationFormRoute/quotationFormDateTime';
import { quotationContractEndDateLocalFromSources } from './QuotationFormRoute/quotationRecurring';
import { quotationFormSamplesFromDetailLines } from './QuotationFormRoute/TabScreens/Samples';
import {
  quotationFormMoneyTotals,
  quotationFormOtherCostsFromDetailLines,
} from './QuotationFormRoute/TabScreens/BiayaLainnya';

/** Nilai total quotation (grand total) yang memicu persetujuan manajer marketing. */
export const QUOTATION_MANAJER_APPROVAL_THRESHOLD = 20_000_000;

export function quotationRequiresManajerApproval(grandTotal: number): boolean {
  return grandTotal >= QUOTATION_MANAJER_APPROVAL_THRESHOLD;
}

function contractHeaderFromQuotationRow(row: StrapiQuotationRow) {
  return {
    contract_period_start_date: quotationDatetimeLocalFromIso(row.contract_period_start_date),
    contract_period_end_date: quotationContractEndDateLocalFromSources({
      contract_period_end_date: row.contract_period_end_date,
      contract_period_start_date: row.contract_period_start_date,
      contract_period_long: row.contract_period_long,
      contract_period_type: row.contract_period_type,
    }),
  };
}

/** Section sampel untuk preview modal (selaras PDF grouping kontrak/konsultan). */
export function quotationPreviewSampleSectionsFromQuotationRow(
  row: StrapiQuotationRow,
): { usesGroups: boolean; sections: QuotationPreviewSampleSection[] } {
  const usesGroups = Boolean(row.is_contract || row.is_consultant_quote);
  const lines = row.sample_informations ?? [];
  const allOtherCosts = row.quotation_other_costs ?? [];
  const sampleGroups =
    usesGroups
      ? quotationFormGroupsFromDetailLines(lines, allOtherCosts, row.quotation_groups ?? [])
      : [];
  const sampleRows =
    !usesGroups && lines.length ? quotationFormFlatSamplesFromDetailLines(lines) : [];

  const preview = quotationPreviewSampleSectionsFromRow({
    is_contract: Boolean(row.is_contract),
    is_consultant_quote: Boolean(row.is_consultant_quote),
    ...contractHeaderFromQuotationRow(row),
    sampleGroups,
    sampleRows,
  });

  if (usesGroups && sampleGroups.length) {
    const ungrouped = quotationFormSamplesFromDetailLines(
      lines.filter((line) => !line.quotation_group?.documentId),
    );
    if (ungrouped.length) {
      preview.sections.push({
        sectionLabel: 'Lainnya',
        sampleRows: ungrouped,
        recurringCount: 1,
        otherCostRows: [],
      });
    }
  }

  return preview;
}

export function quotationFinancialBreakdownFromRow(row: StrapiQuotationRow): QuotationFormFinancialBreakdown {
  const usesGroups = Boolean(row.is_contract || row.is_consultant_quote);

  if (usesGroups) {
    const sampleGroups = quotationFormGroupsFromDetailLines(
      row.sample_informations ?? [],
      row.quotation_other_costs ?? [],
      row.quotation_groups ?? [],
    );
    return quotationFormFinancialBreakdown({
      is_contract: Boolean(row.is_contract),
      is_consultant_quote: Boolean(row.is_consultant_quote),
      ...contractHeaderFromQuotationRow(row),
      sampleGroups,
      sampleRows: [],
      otherCostRows: [],
    });
  }

  const sampleRows = (row.sample_informations ?? []).length
    ? quotationFormFlatSamplesFromDetailLines(row.sample_informations ?? [])
    : [];
  const otherCostRows = quotationFormOtherCostsFromDetailLines(row.quotation_other_costs ?? []);

  return quotationFormFinancialBreakdown({
    is_contract: false,
    is_consultant_quote: false,
    contract_period_start_date: '',
    contract_period_end_date: '',
    sampleGroups: [],
    sampleRows,
    otherCostRows,
  });
}

/** Ringkasan biaya lengkap dari baris quotation (selaras form, termasuk recurring per group kontrak). */
export function quotationMoneyTotalsFromRow(row: StrapiQuotationRow) {
  const financial = quotationFinancialBreakdownFromRow(row);

  return {
    financial,
    otherCostRows: financial.allOtherCostRows,
    totals: quotationFormMoneyTotals({
      sampleRows: financial.rows,
      otherCostRows: financial.allOtherCostRows,
      discountIsPercentage: false,
      discountAmountStr: String(row.discount_amount ?? 0),
      discountPercentageStr: '0',
      isPpn: Boolean(row.is_ppn),
      sampleRecurringCounts: financial.recurringCounts,
      otherCostRecurringCounts: financial.otherCostRecurringCounts,
    }),
  };
}

/** Hitung grand total dari baris quotation (selaras ringkasan biaya form, termasuk recurring kontrak). */
export function quotationGrandTotalFromRow(row: StrapiQuotationRow): number {
  return quotationMoneyTotalsFromRow(row).totals.grandTotal;
}
